import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Plus,
  Trash2,
  TrendingUp,
  Eye,
  Heart,
  Share2,
  MessageCircle,
  Loader2,
  Globe,
  Tag,
  PlayCircle,
  RefreshCw,
  AlertCircle,
  Search as SearchIcon,
  Users,
  Link2,
  Clock,
  Calendar,
  ArrowUp,
  ArrowDown,
  Zap,
  Download,
  Wand2,
  FileText,
  BarChart2,
  Upload,
  Flame,
} from "lucide-react";

import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import useAIVideoAnalyst from "../../../api/aiVideoAnalyst";
import { cn } from "../../../lib/utils";
import { parseUTCDate } from "../../../lib/utils";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Textarea } from "../../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";

import AccountsTab from "./AccountsTab";
import PreviewScoreDialog from "./PreviewScoreDialog";
import VideoDetailDialog from "./VideoDetailDialog";
import HookGeneratorDialog from "./HookGeneratorDialog";
import ScriptGeneratorDialog from "./ScriptGeneratorDialog";
import BulkImportPanel from "./BulkImportPanel";
import TrendsTab from "./TrendsTab";
import BestPostTimePanel from "./BestPostTimePanel";
import HookPatternsPanel from "./HookPatternsPanel";
import CalendarTab from "./CalendarTab";
import BuyCreditsDialog from "./BuyCreditsDialog";
import ViralDiscoverTab from "./ViralDiscoverTab";

// ---------- constants ----------

const PLATFORMS = [
  { key: "all", label: "Alle" },
  { key: "tiktok", label: "TikTok" },
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook" },
  { key: "youtube", label: "YouTube" },
];

const SECTORS = [
  "business",
  "fitness",
  "e-commerce",
  "food",
  "fashion",
  "tech",
  "travel",
  "education",
  "beauty",
  "real-estate",
  "automotive",
  "entertainment",
];

const COUNTRIES = [
  { code: "BE", label: "België" },
  { code: "NL", label: "Nederland" },
  { code: "FR", label: "Frankrijk" },
  { code: "DE", label: "Duitsland" },
  { code: "GB", label: "Verenigd Koninkrijk" },
  { code: "US", label: "Verenigde Staten" },
];

const INSIGHT_TYPES = [
  { key: "hooks", label: "Hooks" },
  { key: "formats", label: "Formats" },
  { key: "themes", label: "Thema's" },
  { key: "overall", label: "Overall strategie" },
];

const SORT_OPTIONS = [
  { key: "views", label: "Views", Icon: Eye },
  { key: "engagement_rate", label: "Engagement", Icon: TrendingUp },
  { key: "likes", label: "Likes", Icon: Heart },
  { key: "shares", label: "Shares", Icon: Share2 },
  { key: "comments", label: "Comments", Icon: MessageCircle },
  { key: "recent", label: "Datum", Icon: Calendar },
  { key: "duration", label: "Duur", Icon: Clock },
  { key: "added", label: "Toegevoegd", Icon: Plus },
];

// ---------- helpers ----------

const formatCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
};

const formatPercent = (rate) => `${((Number(rate) || 0) * 100).toFixed(1)}%`;

const platformColor = (p) => {
  switch (p) {
    case "tiktok":
      return "bg-pink-500/15 text-pink-500 border-pink-500/20";
    case "instagram":
      return "bg-purple-500/15 text-purple-500 border-purple-500/20";
    case "facebook":
      return "bg-blue-500/15 text-blue-500 border-blue-500/20";
    case "youtube":
      return "bg-red-500/15 text-red-500 border-red-500/20";
    default:
      return "bg-gray-500/15 text-gray-500 border-gray-500/20";
  }
};

// ---------- empty default state for forms ----------

const EMPTY_VIDEO = {
  platform: "tiktok",
  url: "",
  author_handle: "",
  thumbnail_url: "",
  hook_text: "",
  caption: "",
  transcript: "",
  sector: "",
  country: "",
  views: 0,
  likes: 0,
  shares: 0,
  comments: 0,
  duration_seconds: "",
  published_at: "",
};

// ============================================================
// Main component
// ============================================================

const AIVideoAnalystManager = () => {
  const { company } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const {
    getVideos,
    addVideo,
    bulkAddVideos,
    autoScoreVideos,
    deleteVideo,
    fetchUrlMetadata,
    exportVideosCsv,
    getStats,
    getTrends,
    getBestPostTime,
    getHookPatterns,
    getInsights,
    generateInsights,
    generateHooks,
    generateScript,
    previewScore,
    savePreviewScore,
    getCredits,
    buyCredits,
    verifyCredits,
    listAccounts,
    connectMeta,
    connectTikTok,
    connectYouTube,
    syncAccount,
    deleteAccount,
    discoverViral,
    analyseViralTrends,
    generateViralIdea,
    isLoading,
    error,
  } = useAIVideoAnalyst();

  // --------- viral discover state (persists across tab switches) ---------
  const [viralVideos,   setViralVideos]   = useState([]);
  const [viralAnalysis, setViralAnalysis] = useState(null);
  const [viralIdea,     setViralIdea]     = useState(null);
  const [viralFilters,  setViralFilters]  = useState({ platform: "both", niche: "", language: "nl", period: "week" });
  const [viralSearched, setViralSearched] = useState(false);

  // --------- state ---------
  const [activeTab, setActiveTab] = useState(() => {
    // If returning from Meta OAuth, jump to accounts tab so the banner shows immediately.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("meta_status")) return "accounts";
    }
    return "videos";
  });

  const [filters, setFilters] = useState({
    sector: "",
    country: "",
    platform: "all",
    date_from: "",
    date_to: "",
    sort: "views",
    sort_dir: "desc",
    search: "",
  });
  const [videos, setVideos] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [credits, setCredits] = useState(null);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showHookDialog, setShowHookDialog] = useState(false);
  const [showScriptDialog, setShowScriptDialog] = useState(false);
  const [showBuyCreditsDialog, setShowBuyCreditsDialog] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [autoScoring, setAutoScoring] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    insight_type: "hooks",
    sector: "",
    country: "",
    date_from: "",
    date_to: "",
  });

  const [newVideo, setNewVideo] = useState(EMPTY_VIDEO);
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);

  // --------- queries ---------
  const queryFilters = useMemo(
    () => ({
      sector: filters.sector,
      country: filters.country,
      platform: filters.platform === "all" ? "" : filters.platform,
      date_from: filters.date_from,
      date_to: filters.date_to,
      sort: filters.sort,
      sort_dir: filters.sort_dir,
      search: filters.search,
      page: pagination.page,
      limit: 24,
    }),
    [filters, pagination.page]
  );

  const refetchVideos = useCallback(async () => {
    try {
      const data = await getVideos(queryFilters);
      setVideos(data.items || []);
      setPagination((p) => ({
        ...p,
        totalPages: data.totalPages || 1,
        total: data.total || 0,
      }));
    } catch (_e) {
      // error state is captured in hook
    }
  }, [getVideos, queryFilters]);

  const refetchStats = useCallback(async () => {
    try {
      const data = await getStats({
        sector: filters.sector,
        country: filters.country,
        platform: filters.platform === "all" ? "" : filters.platform,
        date_from: filters.date_from,
        date_to: filters.date_to,
      });
      setStats(data);
    } catch (_e) {
      // ignore
    }
  }, [getStats, filters]);

  const refetchInsights = useCallback(async () => {
    try {
      const data = await getInsights({
        sector: filters.sector,
        country: filters.country,
      });
      setInsights(data || []);
    } catch (_e) {
      // ignore
    }
  }, [getInsights, filters.sector, filters.country]);

  const refetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const data = await listAccounts();
      setAccounts(data || []);
    } catch (_e) {
      // ignore
    } finally {
      setAccountsLoading(false);
    }
  }, [listAccounts]);

  const refetchCredits = useCallback(async () => {
    try {
      const data = await getCredits();
      setCredits(data);
    } catch (_e) {
      // ignore
    }
  }, [getCredits]);

  useEffect(() => {
    refetchVideos();
  }, [refetchVideos]);

  useEffect(() => {
    refetchStats();
    refetchInsights();
    // reset to page 1 when filters that aren't `page` change
    setPagination((p) => ({ ...p, page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.sector,
    filters.country,
    filters.platform,
    filters.date_from,
    filters.date_to,
    filters.sort,
    filters.sort_dir,
    filters.search,
  ]);

  useEffect(() => {
    refetchAccounts();
  }, [refetchAccounts]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("credits_status") === "success") {
      // Clean URL first
      const url = new URL(window.location.href);
      url.searchParams.delete("credits_status");
      url.searchParams.delete("order_id");
      window.history.replaceState({}, "", url.toString());
      // Verify payment with Mollie and credit tokens
      verifyCredits()
        .then((data) => setCredits(data))
        .catch(() => refetchCredits());
    } else {
      refetchCredits();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------- handlers ---------
  const handleAdd = async () => {
    setAddError(null);
    setAdding(true);
    try {
      const payload = {
        ...newVideo,
        views: Number(newVideo.views) || 0,
        likes: Number(newVideo.likes) || 0,
        shares: Number(newVideo.shares) || 0,
        comments: Number(newVideo.comments) || 0,
        duration_seconds:
          newVideo.duration_seconds === "" ? null : Number(newVideo.duration_seconds),
        published_at: newVideo.published_at || null,
        country: newVideo.country || null,
        sector: newVideo.sector || null,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await addVideo(payload);
      setShowAddDialog(false);
      setNewVideo(EMPTY_VIDEO);
      await Promise.all([refetchVideos(), refetchStats()]);
    } catch (e) {
      setAddError(
        e?.response?.data?.message ||
          e?.response?.data?.details?.join(", ") ||
          "Toevoegen mislukt"
      );
    } finally {
      setAdding(false);
    }
  };


  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteVideo(pendingDelete);
      setPendingDelete(null);
      await Promise.all([refetchVideos(), refetchStats()]);
    } catch (_e) {
      setPendingDelete(null);
    }
  };

  const handlePreviewScore = useCallback(
    async (payload) => {
      const result = await previewScore(payload);
      refetchCredits();
      return result;
    },
    [previewScore, refetchCredits]
  );

  const handleSavePreviewScore = useCallback(
    async (data) => {
      const insight = await savePreviewScore(data);
      refetchInsights();
      return insight;
    },
    [savePreviewScore, refetchInsights]
  );

  const handleAutoScore = useCallback(async () => {
    setAutoScoring(true);
    try {
      await autoScoreVideos();
      await refetchVideos();
    } catch (_e) {
      // error shown via hook
    } finally {
      setAutoScoring(false);
      refetchCredits();
    }
  }, [autoScoreVideos, refetchVideos, refetchCredits]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await exportVideosCsv();
    } catch (_e) {
      // ignore
    } finally {
      setExporting(false);
    }
  }, [exportVideosCsv]);

  const handleGenerateHooks = useCallback(
    async (payload) => {
      const result = await generateHooks(payload);
      refetchCredits();
      return result;
    },
    [generateHooks, refetchCredits]
  );

  const handleGenerateScript = useCallback(
    async (payload) => {
      const result = await generateScript(payload);
      refetchCredits();
      return result;
    },
    [generateScript, refetchCredits]
  );

  const handleGenerateInsights = useCallback(async () => {
    setGenerating(true);
    try {
      await generateInsights({
        insightType: generateForm.insight_type,
        sector: generateForm.sector || null,
        country: generateForm.country || null,
        dateFrom: generateForm.date_from || null,
        dateTo: generateForm.date_to || null,
      });
      setShowGenerateDialog(false);
      await refetchInsights();
      refetchCredits();
    } catch (_e) {
      // error captured by hook
    } finally {
      setGenerating(false);
    }
  }, [generateInsights, generateForm, refetchInsights, refetchCredits]);

  // --------- render ---------
  return (
    <div className="space-y-6">
      {/* ===== Header ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1
            className={cn(
              "text-[22px] font-semibold tracking-tight",
              isDark ? "text-white" : "text-gray-900"
            )}
          >
            AI Video Analist
          </h1>
          <p
            className={cn(
              "text-[13px] mt-0.5",
              isDark ? "text-gray-500" : "text-gray-500"
            )}
          >
            Ontdek welke shortform content werkt, per sector, land en periode.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {/* Credits display */}
          {credits !== null && (
            <button
              onClick={() => setShowBuyCreditsDialog(true)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-colors self-end",
                credits.total === 0
                  ? isDark ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-red-50 border-red-200 text-red-600"
                  : credits.total <= 1
                    ? isDark ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" : "bg-yellow-50 border-yellow-200 text-yellow-700"
                    : isDark ? "bg-indigo-500/10 border-indigo-500/20 text-indigo-300" : "bg-indigo-50 border-indigo-200 text-indigo-700"
              )}
            >
              <Zap className="h-3.5 w-3.5" />
              <span>{credits.dailyRemaining} gratis vandaag</span>
              {credits.purchased > 0 && (
                <span className={cn("ml-1 opacity-70")}>+ {credits.purchased} gekocht</span>
              )}
              <span className={cn("ml-1.5 text-[10px] opacity-60 underline")}>Meer kopen</span>
            </button>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowHookDialog(true)} className="gap-1.5">
              <Wand2 className="h-3.5 w-3.5" />
              Hooks
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowScriptDialog(true)} className="gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Script
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPreviewDialog(true)} className="gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Preview score
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowGenerateDialog(true)} className="gap-1.5">
              <BarChart2 className="h-3.5 w-3.5" />
              Inzichten
            </Button>
            <Button size="sm" onClick={() => setShowAddDialog(true)} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Video toevoegen
            </Button>
          </div>
        </div>
      </div>

      {/* ===== Stats row ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          isDark={isDark}
          icon={PlayCircle}
          label="Totaal video's"
          value={stats?.totals?.totalVideos ?? "–"}
        />
        <StatTile
          isDark={isDark}
          icon={Eye}
          label="Gem. views"
          value={formatCount(stats?.totals?.avgViews)}
        />
        <StatTile
          isDark={isDark}
          icon={Users}
          label="Gem. volgers"
          value={formatCount(stats?.totals?.avgFollowers)}
        />
        <StatTile
          isDark={isDark}
          icon={Heart}
          label="Gem. engagement"
          value={formatPercent(stats?.totals?.avgEngagementRate)}
        />
      </div>

      {/* ===== Tabs ===== */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-y-1">
          <TabsTrigger value="videos" className="gap-1.5">
            <PlayCircle className="h-3.5 w-3.5" />
            Video's
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-1.5">
            <BarChart2 className="h-3.5 w-3.5" />
            Analyse
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            Kalender
          </TabsTrigger>
          <TabsTrigger value="accounts" className="gap-1.5">
            <Link2 className="h-3.5 w-3.5" />
            Accounts
            {accounts.length > 0 && (
              <Badge variant="outline" className="ml-1 h-[16px] text-[9px] px-1">
                {accounts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Inzichten
            {insights.length > 0 && (
              <Badge variant="outline" className="ml-1 h-[16px] text-[9px] px-1">
                {insights.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="discover" className="gap-1.5">
            <Flame className="h-3.5 w-3.5" />
            Viraal ontdekken
          </TabsTrigger>
        </TabsList>

        {/* ----- Videos tab ----- */}
        <TabsContent value="videos" className="space-y-4">
          {/* Video library toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowBulkImport((v) => !v)} className="gap-1.5">
              <Upload className="h-3.5 w-3.5" />
              Bulk import
            </Button>
            <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting} className="gap-1.5">
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Exporteren
            </Button>
            <Button size="sm" variant="outline" onClick={handleAutoScore} disabled={autoScoring} className="gap-1.5">
              {autoScoring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Auto-score
            </Button>
          </div>

          {/* Bulk import panel */}
          {showBulkImport && (
            <BulkImportPanel
              isDark={isDark}
              bulkAddVideos={bulkAddVideos}
              onDone={() => { setShowBulkImport(false); refetchVideos(); refetchStats(); }}
              onClose={() => setShowBulkImport(false)}
            />
          )}

          {/* Filters */}
          <div
            className={cn(
              "rounded-xl border p-3 space-y-3",
              isDark
                ? "bg-white/[0.02] border-white/[0.06]"
                : "bg-white border-gray-200/70"
            )}
          >
            {/* Row 1: sector, country, dates */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Select
                value={filters.sector || "all"}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, sector: v === "all" ? "" : v }))
                }
              >
                <SelectTrigger className="h-9 text-[13px]">
                  <SelectValue placeholder="Sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle sectoren</SelectItem>
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.country || "all"}
                onValueChange={(v) =>
                  setFilters((f) => ({ ...f, country: v === "all" ? "" : v }))
                }
              >
                <SelectTrigger className="h-9 text-[13px]">
                  <SelectValue placeholder="Land" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle landen</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, date_from: e.target.value }))
                }
                className="h-9 text-[13px]"
              />
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, date_to: e.target.value }))
                }
                className="h-9 text-[13px]"
              />
            </div>

            {/* Row 2: search */}
            <div className="relative">
              <SearchIcon
                className={cn(
                  "absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none",
                  isDark ? "text-gray-500" : "text-gray-400"
                )}
              />
              <Input
                placeholder="Zoek op hook, caption of account..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search: e.target.value }))
                }
                className="h-9 pl-8 text-[13px]"
              />
            </div>

            {/* Row 3: platform + sort (compact) */}
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={filters.platform}
                onValueChange={(v) => setFilters((f) => ({ ...f, platform: v }))}
              >
                <SelectTrigger className="h-8 text-[12px] w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.sort}
                onValueChange={(v) => setFilters((f) => ({ ...f, sort: v }))}
              >
                <SelectTrigger className="h-8 text-[12px] w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(({ key, label }) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <button
                onClick={() => setFilters((f) => ({ ...f, sort_dir: f.sort_dir === "asc" ? "desc" : "asc" }))}
                className={cn(
                  "flex items-center gap-1 h-8 px-2.5 rounded-md text-[12px] border transition-colors",
                  isDark ? "bg-white/[0.03] border-white/[0.08] text-gray-300 hover:bg-white/[0.06]"
                         : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                )}
              >
                {filters.sort_dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                {filters.sort_dir === "asc" ? "Oplopend" : "Aflopend"}
              </button>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div
              className={cn(
                "rounded-lg border p-3 text-[13px] flex items-start gap-2",
                isDark
                  ? "bg-red-500/[0.08] border-red-500/20 text-red-300"
                  : "bg-red-50 border-red-200 text-red-700"
              )}
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                {error?.response?.data?.message || error.message || "Er ging iets mis"}
              </span>
            </div>
          )}

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {isLoading && videos.length === 0 && (
              <div className="col-span-full flex items-center justify-center py-16">
                <Loader2
                  className={cn(
                    "h-6 w-6 animate-spin",
                    isDark ? "text-gray-500" : "text-gray-400"
                  )}
                />
              </div>
            )}

            {!isLoading && videos.length === 0 && (
              <div
                className={cn(
                  "col-span-full text-center py-16 rounded-xl border-2 border-dashed",
                  isDark
                    ? "border-white/[0.06] text-gray-500"
                    : "border-gray-200 text-gray-500"
                )}
              >
                <PlayCircle className="h-7 w-7 mx-auto mb-2 opacity-50" />
                <p className="text-[13px] font-medium">Nog geen video's geanalyseerd</p>
                <p className="text-[12px] mt-0.5">
                  Voeg er een toe of koppel een account.
                </p>
              </div>
            )}

            {videos.map((v) => (
              <VideoCard
                key={v.id}
                video={v}
                isDark={isDark}
                onDelete={() => setPendingDelete(v.id)}
                onOpen={() => setSelectedVideo(v)}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() =>
                  setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))
                }
              >
                Vorige
              </Button>
              <span
                className={cn(
                  "text-[12px]",
                  isDark ? "text-gray-400" : "text-gray-500"
                )}
              >
                Pagina {pagination.page} van {pagination.totalPages} ({pagination.total} totaal)
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() =>
                  setPagination((p) => ({
                    ...p,
                    page: Math.min(pagination.totalPages, p.page + 1),
                  }))
                }
              >
                Volgende
              </Button>
            </div>
          )}
        </TabsContent>

        {/* ----- Trends tab ----- */}
        <TabsContent value="trends">
          <TrendsTab isDark={isDark} getTrends={getTrends} filters={filters} />
        </TabsContent>

        {/* ----- Analytics tab ----- */}
        <TabsContent value="analytics" className="space-y-4">
          <BestPostTimePanel isDark={isDark} getBestPostTime={getBestPostTime} filters={filters} />
          <HookPatternsPanel isDark={isDark} getHookPatterns={getHookPatterns} filters={filters} />
        </TabsContent>

        {/* ----- Calendar tab ----- */}
        <TabsContent value="calendar">
          <CalendarTab isDark={isDark} />
        </TabsContent>

        {/* ----- Accounts tab ----- */}
        <TabsContent value="accounts">
          <AccountsTab
            isDark={isDark}
            accounts={accounts}
            onRefresh={refetchAccounts}
            connectMeta={connectMeta}
            connectTikTok={connectTikTok}
            connectYouTube={connectYouTube}
            syncAccount={syncAccount}
            deleteAccount={deleteAccount}
            loadingAccounts={accountsLoading}
          />
        </TabsContent>

        {/* ----- Insights tab ----- */}
        <TabsContent value="insights">
          <InsightsPanel
            isDark={isDark}
            insights={insights}
            onRefresh={refetchInsights}
            onGenerateClick={() => setShowGenerateDialog(true)}
          />
        </TabsContent>

        {/* ----- Viral Discover tab ----- */}
        <TabsContent value="discover">
          <ViralDiscoverTab
            isDark={isDark}
            discoverViral={discoverViral}
            analyseViralTrends={analyseViralTrends}
            generateViralIdea={generateViralIdea}
            videos={viralVideos}
            setVideos={setViralVideos}
            analysis={viralAnalysis}
            setAnalysis={setViralAnalysis}
            idea={viralIdea}
            setIdea={setViralIdea}
            filters={viralFilters}
            setFilters={setViralFilters}
            searched={viralSearched}
            setSearched={setViralSearched}
          />
        </TabsContent>
      </Tabs>

      {/* ===== Add video dialog ===== */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Video toevoegen</DialogTitle>
            <DialogDescription>
              Vul de metadata van een shortform video in. AI-inzichten gebruiken hook,
              caption, transcript en metrics.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Platform">
              <Select
                value={newVideo.platform}
                onValueChange={(v) => setNewVideo((s) => ({ ...s, platform: v }))}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.filter((p) => p.key !== "all").map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="URL" required>
              <Input
                value={newVideo.url}
                onChange={(e) => setNewVideo((s) => ({ ...s, url: e.target.value }))}
                placeholder="https://www.tiktok.com/@..."
              />
            </Field>

            <Field label="Auteur (@handle)">
              <Input
                value={newVideo.author_handle}
                onChange={(e) =>
                  setNewVideo((s) => ({ ...s, author_handle: e.target.value }))
                }
                placeholder="@gebruikersnaam"
              />
            </Field>

            <Field label="Thumbnail URL">
              <Input
                value={newVideo.thumbnail_url}
                onChange={(e) =>
                  setNewVideo((s) => ({ ...s, thumbnail_url: e.target.value }))
                }
                placeholder="https://..."
              />
            </Field>

            <Field label="Sector">
              <Select
                value={newVideo.sector || "none"}
                onValueChange={(v) =>
                  setNewVideo((s) => ({ ...s, sector: v === "none" ? "" : v }))
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Kies sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen</SelectItem>
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Land">
              <Select
                value={newVideo.country || "none"}
                onValueChange={(v) =>
                  setNewVideo((s) => ({ ...s, country: v === "none" ? "" : v }))
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Kies land" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Views">
              <Input
                type="number"
                min="0"
                value={newVideo.views}
                onChange={(e) =>
                  setNewVideo((s) => ({ ...s, views: e.target.value }))
                }
              />
            </Field>
            <Field label="Likes">
              <Input
                type="number"
                min="0"
                value={newVideo.likes}
                onChange={(e) =>
                  setNewVideo((s) => ({ ...s, likes: e.target.value }))
                }
              />
            </Field>
            <Field label="Shares">
              <Input
                type="number"
                min="0"
                value={newVideo.shares}
                onChange={(e) =>
                  setNewVideo((s) => ({ ...s, shares: e.target.value }))
                }
              />
            </Field>
            <Field label="Comments">
              <Input
                type="number"
                min="0"
                value={newVideo.comments}
                onChange={(e) =>
                  setNewVideo((s) => ({ ...s, comments: e.target.value }))
                }
              />
            </Field>

            <Field label="Duur (sec)">
              <Input
                type="number"
                min="0"
                value={newVideo.duration_seconds}
                onChange={(e) =>
                  setNewVideo((s) => ({ ...s, duration_seconds: e.target.value }))
                }
              />
            </Field>
            <Field label="Gepubliceerd op">
              <Input
                type="date"
                value={newVideo.published_at}
                onChange={(e) =>
                  setNewVideo((s) => ({ ...s, published_at: e.target.value }))
                }
              />
            </Field>

            <div className="sm:col-span-2">
              <Field label="Hook (eerste 1-3 seconden / openingszin)">
                <Input
                  value={newVideo.hook_text}
                  onChange={(e) =>
                    setNewVideo((s) => ({ ...s, hook_text: e.target.value }))
                  }
                  placeholder="bv. 'Niemand vertelt je dit over...'"
                />
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Field label="Caption">
                <Textarea
                  value={newVideo.caption}
                  onChange={(e) =>
                    setNewVideo((s) => ({ ...s, caption: e.target.value }))
                  }
                  rows={2}
                />
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Field label="Transcript (optioneel — verbetert AI inzichten)">
                <Textarea
                  value={newVideo.transcript}
                  onChange={(e) =>
                    setNewVideo((s) => ({ ...s, transcript: e.target.value }))
                  }
                  rows={4}
                />
              </Field>
            </div>
          </div>

          {addError && (
            <div
              className={cn(
                "rounded-md border p-2 text-[12px]",
                isDark
                  ? "bg-red-500/[0.08] border-red-500/20 text-red-300"
                  : "bg-red-50 border-red-200 text-red-700"
              )}
            >
              {addError}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuleren
            </Button>
            <Button onClick={handleAdd} disabled={adding || !newVideo.url}>
              {adding && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Generate insights dialog ===== */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Inzichten genereren
            </DialogTitle>
            <DialogDescription>
              Claude analyseert je top videos en geeft patronen terug per categorie.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Field label="Type inzicht">
              <Select
                value={generateForm.insight_type}
                onValueChange={(v) =>
                  setGenerateForm((s) => ({ ...s, insight_type: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSIGHT_TYPES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Sector">
                <Select
                  value={generateForm.sector || "all"}
                  onValueChange={(v) =>
                    setGenerateForm((s) => ({
                      ...s,
                      sector: v === "all" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    {SECTORS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Land">
                <Select
                  value={generateForm.country || "all"}
                  onValueChange={(v) =>
                    setGenerateForm((s) => ({
                      ...s,
                      country: v === "all" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Periode vanaf">
                <Input
                  type="date"
                  value={generateForm.date_from}
                  onChange={(e) =>
                    setGenerateForm((s) => ({ ...s, date_from: e.target.value }))
                  }
                />
              </Field>
              <Field label="Periode tot">
                <Input
                  type="date"
                  value={generateForm.date_to}
                  onChange={(e) =>
                    setGenerateForm((s) => ({ ...s, date_to: e.target.value }))
                  }
                />
              </Field>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Annuleren
            </Button>
            <Button onClick={handleGenerateInsights} disabled={generating}>
              {generating && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Genereer met AI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Preview score dialog ===== */}
      <PreviewScoreDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        previewScore={handlePreviewScore}
        savePreviewScore={handleSavePreviewScore}
        isDark={isDark}
      />

      {/* ===== Video detail dialog ===== */}
      <VideoDetailDialog
        open={!!selectedVideo}
        onOpenChange={(o) => !o && setSelectedVideo(null)}
        video={selectedVideo}
        isDark={isDark}
      />

      {/* ===== Hook generator ===== */}
      <HookGeneratorDialog
        open={showHookDialog}
        onOpenChange={setShowHookDialog}
        generateHooks={handleGenerateHooks}
        isDark={isDark}
      />

      {/* ===== Script generator ===== */}
      <ScriptGeneratorDialog
        open={showScriptDialog}
        onOpenChange={setShowScriptDialog}
        generateScript={handleGenerateScript}
        isDark={isDark}
      />

      {/* ===== Buy credits ===== */}
      <BuyCreditsDialog
        open={showBuyCreditsDialog}
        onOpenChange={(o) => { setShowBuyCreditsDialog(o); if (!o) refetchCredits(); }}
        buyCredits={buyCredits}
        isDark={isDark}
      />

      {/* ===== Delete confirm ===== */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Video verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============================================================
// Sub-components
// ============================================================

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

const StatTile = ({ icon: Icon, label, value, isDark }) => (
  <motion.div
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    className={cn(
      "rounded-xl border p-3 flex items-center gap-3",
      isDark
        ? "bg-white/[0.02] border-white/[0.06]"
        : "bg-white border-gray-200/70"
    )}
  >
    <div
      className={cn(
        "h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0",
        isDark ? "bg-white/[0.06]" : "bg-gray-50"
      )}
    >
      <Icon
        className={cn("h-4 w-4", isDark ? "text-gray-300" : "text-gray-700")}
      />
    </div>
    <div className="min-w-0">
      <p
        className={cn(
          "text-[10px] uppercase tracking-wider font-semibold",
          isDark ? "text-gray-500" : "text-gray-400"
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "text-[15px] font-semibold truncate",
          isDark ? "text-white" : "text-gray-900"
        )}
      >
        {value}
      </p>
    </div>
  </motion.div>
);

const VideoCard = ({ video, isDark, onDelete, onOpen }) => {
  const engagement = formatPercent(video.engagement_rate);
  const publishedDate = video.published_at
    ? parseUTCDate(video.published_at)
    : null;
  const followers = Number(video.account_followers) || 0;

  const handleCardClick = () => {
    if (typeof onOpen === "function") onOpen();
  };

  const handleCardKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      className={cn(
        "rounded-xl border overflow-hidden flex flex-col group cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40",
        "transition-colors",
        isDark
          ? "bg-white/[0.02] border-white/[0.06] hover:border-white/[0.18]"
          : "bg-white border-gray-200/70 hover:border-gray-300"
      )}
    >
      <div
        className={cn(
          "aspect-[9/16] max-h-56 relative bg-gradient-to-br",
          isDark
            ? "from-gray-900 to-black"
            : "from-gray-100 to-gray-200"
        )}
      >
        {video.thumbnail_url ? (
          <img
            src={video.thumbnail_url}
            alt={video.hook_text || "video"}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <PlayCircle
              className={cn(
                "h-10 w-10 opacity-30",
                isDark ? "text-white" : "text-gray-400"
              )}
            />
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <span
            className={cn(
              "px-1.5 py-0.5 rounded text-[10px] font-semibold border backdrop-blur-md capitalize",
              platformColor(video.platform)
            )}
          >
            {video.platform}
          </span>
          {video.sector && (
            <span
              className={cn(
                "px-1.5 py-0.5 rounded text-[10px] font-medium border backdrop-blur-md",
                isDark
                  ? "bg-black/40 text-white border-white/20"
                  : "bg-white/80 text-gray-700 border-white"
              )}
            >
              {video.sector}
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}
          className={cn(
            "absolute top-2 right-2 p-1 rounded backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity",
            "bg-black/40 text-white hover:bg-red-500/80"
          )}
          aria-label="Verwijderen"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        <p
          className={cn(
            "text-[12px] font-medium line-clamp-2 min-h-[2.5em]",
            isDark ? "text-gray-200" : "text-gray-800"
          )}
        >
          {video.hook_text || video.caption?.slice(0, 80) || "Geen hook"}
        </p>

        <div className="flex items-center justify-between gap-2 text-[11px]">
          <div className="flex items-center gap-2">
            {video.author_handle && (
              <span
                className={cn(
                  "truncate max-w-[120px]",
                  isDark ? "text-gray-500" : "text-gray-500"
                )}
              >
                {video.author_handle}
              </span>
            )}
          </div>
          {video.country && (
            <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">
              <Globe className="h-2.5 w-2.5 mr-1" />
              {video.country}
            </Badge>
          )}
        </div>

        <div
          className={cn(
            "grid grid-cols-4 gap-1.5 pt-2 border-t",
            isDark ? "border-white/[0.06]" : "border-gray-100"
          )}
        >
          <Metric icon={Eye} value={formatCount(video.views)} isDark={isDark} />
          <Metric icon={Heart} value={formatCount(video.likes)} isDark={isDark} />
          <Metric icon={Share2} value={formatCount(video.shares)} isDark={isDark} />
          <Metric
            icon={MessageCircle}
            value={formatCount(video.comments)}
            isDark={isDark}
          />
        </div>

        <div className="flex items-center justify-between text-[11px] pt-1">
          <span
            className={cn(
              "font-semibold",
              isDark ? "text-emerald-400" : "text-emerald-600"
            )}
          >
            {engagement} engagement
          </span>
          {followers > 0 ? (
            <span
              className={cn(
                "flex items-center gap-1",
                isDark ? "text-gray-500" : "text-gray-500"
              )}
              title="Volgers op moment van publicatie"
            >
              <Users className="h-2.5 w-2.5" />
              {formatCount(followers)}
            </span>
          ) : publishedDate ? (
            <span className={isDark ? "text-gray-600" : "text-gray-400"}>
              {publishedDate.toLocaleDateString("nl-BE")}
            </span>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
};

const Metric = ({ icon: Icon, value, isDark }) => (
  <div className="flex items-center gap-1">
    <Icon className={cn("h-3 w-3", isDark ? "text-gray-500" : "text-gray-400")} />
    <span
      className={cn(
        "text-[11px] font-medium",
        isDark ? "text-gray-300" : "text-gray-700"
      )}
    >
      {value}
    </span>
  </div>
);

const NICHE_LABELS_MAP = {
  auto: "Automotive", mode: "Mode & Fashion", food: "Food & Horeca",
  fitness: "Sport & Fitness", beauty: "Beauty & Wellness", reizen: "Reizen",
  interieur: "Interieur", business: "Business", tech: "Technologie",
  muziek: "Muziek", immo: "Vastgoed", gezondheid: "Gezondheid",
  diy: "DIY", gaming: "Gaming", dieren: "Dieren", kunst: "Kunst",
  marketing: "Marketing", sport: "Sport",
};

const ViralTrendsCard = ({ insight, isDark }) => {
  const c = insight.content || {};
  const generatedAt = parseUTCDate(insight.generated_at);
  return (
    <div className={cn("rounded-lg border p-3 space-y-2", isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50/50 border-gray-200/70")}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 flex-wrap">
          <TrendingUp className={cn("h-3 w-3", isDark ? "text-orange-400" : "text-orange-500")} />
          <span className={cn("text-[11px] font-semibold uppercase tracking-wider", isDark ? "text-orange-300" : "text-orange-700")}>Viral Analyse</span>
          {c.niche && <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">{NICHE_LABELS_MAP[c.niche] || c.niche}</Badge>}
          {c.platform && <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">{c.platform}</Badge>}
          {c.period && <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">{c.period}</Badge>}
        </div>
        <span className={cn("text-[10px]", isDark ? "text-gray-600" : "text-gray-400")}>{generatedAt ? generatedAt.toLocaleDateString("nl-BE") : ""}</span>
      </div>
      {c.samenvatting && <p className={cn("text-[12px] leading-relaxed", isDark ? "text-gray-300" : "text-gray-700")}>{c.samenvatting}</p>}
      {c.trending_themas?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {c.trending_themas.map((t, i) => <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>)}
        </div>
      )}
      {c.hoe_inspelen?.length > 0 && (
        <ul className="space-y-0.5">
          {c.hoe_inspelen.slice(0, 2).map((tip, i) => (
            <li key={i} className={cn("text-[11.5px] flex items-start gap-1", isDark ? "text-gray-400" : "text-gray-600")}>
              <span className="text-emerald-500 mt-0.5">✓</span>{tip}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ViralIdeaCard = ({ insight, isDark }) => {
  const c = insight.content || {};
  const generatedAt = parseUTCDate(insight.generated_at);
  const [copied, setCopied] = useState(false);
  const copyCaption = () => {
    if (!c.caption_voorstel) return;
    navigator.clipboard.writeText(c.caption_voorstel).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <div className={cn("rounded-lg border p-3 space-y-2", isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50/50 border-gray-200/70")}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Wand2 className={cn("h-3 w-3", isDark ? "text-purple-400" : "text-purple-500")} />
          <span className={cn("text-[11px] font-semibold uppercase tracking-wider", isDark ? "text-purple-300" : "text-purple-700")}>Video-idee</span>
          {c.niche && <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">{NICHE_LABELS_MAP[c.niche] || c.niche}</Badge>}
          {c.platform && <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">{c.platform}</Badge>}
        </div>
        <span className={cn("text-[10px]", isDark ? "text-gray-600" : "text-gray-400")}>{generatedAt ? generatedAt.toLocaleDateString("nl-BE") : ""}</span>
      </div>
      {c.titel && <p className={cn("text-[13px] font-semibold", isDark ? "text-white" : "text-gray-900")}>{c.titel}</p>}
      {c.hook && <p className={cn("text-[12px] italic", isDark ? "text-purple-300" : "text-purple-700")}>&ldquo;{c.hook}&rdquo;</p>}
      {c.concept && <p className={cn("text-[12px] leading-relaxed", isDark ? "text-gray-400" : "text-gray-600")}>{c.concept}</p>}
      {c.caption_voorstel && (
        <button onClick={copyCaption} className={cn("w-full text-left px-2.5 py-2 rounded-lg text-[11.5px] transition-colors", isDark ? "bg-white/[0.04] hover:bg-white/[0.07] text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-700")}>
          {copied ? "✓ Gekopieerd" : "📋 Kopieer caption"}
        </button>
      )}
    </div>
  );
};

const INSIGHT_TYPE_LABELS = {
  hooks: "Hooks",
  formats: "Formats",
  themes: "Thema's",
  overall: "Overall",
  preview_score: "Preview Score",
  viral_trends: "Viral Analyse",
  viral_idea: "Video-idee",
};

const InsightsPanel = ({ isDark, insights, onRefresh, onGenerateClick }) => {
  const [typeFilter, setTypeFilter] = useState("all");

  const filtered =
    typeFilter === "all" ? insights : insights.filter((i) => i.insight_type === typeFilter);

  const types = ["all", ...Array.from(new Set(insights.map((i) => i.insight_type)))];

  return (
    <div
      className={cn(
        "rounded-xl border",
        isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/70"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between px-4 py-3 border-b",
          isDark ? "border-white/[0.06]" : "border-gray-100"
        )}
      >
        <div className="flex items-center gap-2">
          <Sparkles
            className={cn("h-4 w-4", isDark ? "text-blue-400" : "text-blue-500")}
          />
          <h2
            className={cn(
              "text-[14px] font-semibold",
              isDark ? "text-white" : "text-gray-900"
            )}
          >
            AI Inzichten
          </h2>
          <Badge variant="outline" className="text-[10px] h-[18px] px-1.5">
            {insights.length}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            className="h-7 gap-1 text-[12px]"
          >
            <RefreshCw className="h-3 w-3" />
            Vernieuwen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onGenerateClick}
            className="h-7 gap-1 text-[12px]"
          >
            <Sparkles className="h-3 w-3" />
            Nieuw
          </Button>
        </div>
      </div>

      {types.length > 2 && (
        <div
          className={cn(
            "flex flex-wrap items-center gap-1.5 px-4 py-2 border-b",
            isDark ? "border-white/[0.06]" : "border-gray-100"
          )}
        >
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={cn(
                "px-2.5 py-0.5 rounded-full text-[11px] font-medium border transition-colors",
                typeFilter === t
                  ? isDark
                    ? "bg-white/[0.08] text-white border-white/[0.12]"
                    : "bg-gray-900 text-white border-gray-900"
                  : isDark
                    ? "bg-transparent text-gray-400 border-white/[0.08] hover:bg-white/[0.04]"
                    : "bg-transparent text-gray-600 border-gray-200 hover:bg-gray-50"
              )}
            >
              {t === "all" ? "Alles" : (INSIGHT_TYPE_LABELS[t] || t)}
            </button>
          ))}
        </div>
      )}

      <div className="p-3">
        {filtered.length === 0 ? (
          <div
            className={cn(
              "text-center py-10 text-[13px]",
              isDark ? "text-gray-500" : "text-gray-500"
            )}
          >
            {insights.length === 0
              ? <>Nog geen inzichten — klik op <strong>Nieuw</strong> om Claude je top performers te laten analyseren.</>
              : "Geen inzichten voor dit filter."}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map((ins) =>
              ins.insight_type === "preview_score" ? (
                <PreviewScoreCard key={ins.id} insight={ins} isDark={isDark} />
              ) : ins.insight_type === "viral_trends" ? (
                <ViralTrendsCard key={ins.id} insight={ins} isDark={isDark} />
              ) : ins.insight_type === "viral_idea" ? (
                <ViralIdeaCard key={ins.id} insight={ins} isDark={isDark} />
              ) : (
                <InsightCard key={ins.id} insight={ins} isDark={isDark} />
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const scoreColor = (score, isDark) => {
  if (score >= 75) return isDark ? "text-emerald-400" : "text-emerald-600";
  if (score >= 50) return isDark ? "text-yellow-400" : "text-yellow-600";
  return isDark ? "text-red-400" : "text-red-600";
};

const PreviewScoreCard = ({ insight, isDark }) => {
  const c = insight.content || {};
  const input = c.input || {};
  const generatedAt = parseUTCDate(insight.generated_at);

  return (
    <div
      className={cn(
        "rounded-lg border p-3",
        isDark
          ? "bg-white/[0.03] border-white/[0.06]"
          : "bg-gray-50/50 border-gray-200/70"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className={cn("h-3 w-3", isDark ? "text-indigo-400" : "text-indigo-500")} />
          <span
            className={cn(
              "text-[11px] font-semibold uppercase tracking-wider",
              isDark ? "text-indigo-300" : "text-indigo-700"
            )}
          >
            Preview Score
          </span>
          {insight.sector && (
            <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">
              {insight.sector}
            </Badge>
          )}
          {c.frames_analyzed > 0 && (
            <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">
              {c.frames_analyzed} frames
            </Badge>
          )}
        </div>
        <span className={cn("text-[28px] font-bold leading-none", scoreColor(c.predicted_score, isDark))}>
          {Math.round(c.predicted_score ?? 0)}
          <span className={cn("text-[12px] ml-0.5", isDark ? "text-gray-500" : "text-gray-400")}>/100</span>
        </span>
      </div>

      {/* Input summary */}
      {(input.hook_text || input.caption) && (
        <div
          className={cn(
            "rounded-md border p-2 mb-2 space-y-0.5",
            isDark ? "bg-white/[0.02] border-white/[0.04]" : "bg-white border-gray-100"
          )}
        >
          {input.hook_text && (
            <p className={cn("text-[11px] italic truncate", isDark ? "text-gray-400" : "text-gray-600")}>
              Hook: &ldquo;{input.hook_text}&rdquo;
            </p>
          )}
          {input.caption && (
            <p className={cn("text-[11px] truncate", isDark ? "text-gray-500" : "text-gray-500")}>
              Caption: {input.caption}
            </p>
          )}
        </div>
      )}

      {/* Engagement + visual */}
      <div className="space-y-1.5">
        {c.predicted_engagement_rate && (
          <p className={cn("text-[12px]", isDark ? "text-gray-300" : "text-gray-700")}>
            <span className={cn("font-semibold", isDark ? "text-gray-400" : "text-gray-600")}>Engagement: </span>
            {c.predicted_engagement_rate}
          </p>
        )}
        {c.visual_analysis && (
          <p className={cn("text-[12px]", isDark ? "text-gray-300" : "text-gray-700")}>
            <span className={cn("font-semibold", isDark ? "text-gray-400" : "text-gray-600")}>Visueel: </span>
            {c.visual_analysis}
          </p>
        )}
        {c.hook_analysis && (
          <p className={cn("text-[12px]", isDark ? "text-gray-300" : "text-gray-700")}>
            <span className={cn("font-semibold", isDark ? "text-gray-400" : "text-gray-600")}>Hook: </span>
            {c.hook_analysis}
          </p>
        )}
      </div>

      {/* Strengths + improvements */}
      {(c.strengths?.length > 0 || c.improvements?.length > 0) && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {c.strengths?.length > 0 && (
            <ul className="space-y-0.5">
              {c.strengths.slice(0, 3).map((s, i) => (
                <li key={i} className={cn("text-[11px] flex gap-1", isDark ? "text-gray-300" : "text-gray-700")}>
                  <span className="text-emerald-500">+</span>{s}
                </li>
              ))}
            </ul>
          )}
          {c.improvements?.length > 0 && (
            <ul className="space-y-0.5">
              {c.improvements.slice(0, 3).map((s, i) => (
                <li key={i} className={cn("text-[11px] flex gap-1", isDark ? "text-gray-300" : "text-gray-700")}>
                  <span className="text-yellow-500">!</span>{s}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {generatedAt && (
        <p className={cn("text-[10px] mt-2", isDark ? "text-gray-600" : "text-gray-400")}>
          {generatedAt.toLocaleString("nl-BE")}
        </p>
      )}
    </div>
  );
};

const InsightCard = ({ insight, isDark }) => {
  const items = Array.isArray(insight.content?.items) ? insight.content.items : [];
  const generatedAt = parseUTCDate(insight.generated_at);

  return (
    <div
      className={cn(
        "rounded-lg border p-3",
        isDark
          ? "bg-white/[0.03] border-white/[0.06]"
          : "bg-gray-50/50 border-gray-200/70"
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Tag className={cn("h-3 w-3", isDark ? "text-gray-400" : "text-gray-500")} />
          <span
            className={cn(
              "text-[11px] font-semibold uppercase tracking-wider",
              isDark ? "text-gray-300" : "text-gray-700"
            )}
          >
            {insight.insight_type}
          </span>
          {insight.sector && (
            <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">
              {insight.sector}
            </Badge>
          )}
          {insight.country && (
            <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">
              {insight.country}
            </Badge>
          )}
        </div>
        <span
          className={cn(
            "text-[10px]",
            isDark ? "text-gray-600" : "text-gray-400"
          )}
        >
          {insight.video_count} video's
        </span>
      </div>

      {insight.content?.summary && (
        <p
          className={cn(
            "text-[12px] mb-2 leading-relaxed",
            isDark ? "text-gray-300" : "text-gray-700"
          )}
        >
          {insight.content.summary}
        </p>
      )}

      <ul className="space-y-1.5">
        {items.slice(0, 6).map((item, idx) => (
          <li
            key={idx}
            className={cn(
              "rounded-md border p-2",
              isDark
                ? "bg-white/[0.02] border-white/[0.04]"
                : "bg-white border-gray-100"
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <p
                className={cn(
                  "text-[12px] font-semibold",
                  isDark ? "text-white" : "text-gray-900"
                )}
              >
                {item.title}
              </p>
              {typeof item.score === "number" && (
                <span
                  className={cn(
                    "text-[10px] font-semibold px-1.5 py-0.5 rounded",
                    isDark
                      ? "bg-blue-500/15 text-blue-300"
                      : "bg-blue-50 text-blue-700"
                  )}
                >
                  {Math.round(item.score)}
                </span>
              )}
            </div>
            {item.description && (
              <p
                className={cn(
                  "text-[11px] mt-1",
                  isDark ? "text-gray-400" : "text-gray-600"
                )}
              >
                {item.description}
              </p>
            )}
            {item.why_it_works && (
              <p
                className={cn(
                  "text-[11px] mt-1 italic",
                  isDark ? "text-gray-500" : "text-gray-500"
                )}
              >
                {item.why_it_works}
              </p>
            )}
          </li>
        ))}
      </ul>

      {generatedAt && (
        <p
          className={cn(
            "text-[10px] mt-2",
            isDark ? "text-gray-600" : "text-gray-400"
          )}
        >
          Gegenereerd {generatedAt.toLocaleString("nl-BE")}
        </p>
      )}
    </div>
  );
};

export default AIVideoAnalystManager;
